import type { InferResponseType } from 'hono/client';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import type { api } from '~/lib/api';
import { formatDate } from '~/utils/date';

type FeedbackResponse = InferResponseType<typeof api.api.admin.feedback.$get>;
type FeedbackEntry = FeedbackResponse['feedback'][number];

type FeedbackListProps = {
  feedback: FeedbackEntry[];
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
};

export function FeedbackList({
  feedback,
  total,
  page,
  limit,
  onPageChange
}: FeedbackListProps): React.ReactNode {
  const { t } = useTranslation();
  const totalPages = Math.ceil(total / limit);

  if (feedback.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {t('admin.feedback.noFeedback')}
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="min-w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.rating')}
              </TableHead>
              <TableHead className="min-w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.modality')}
              </TableHead>
              <TableHead className="min-w-24 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.org')}
              </TableHead>
              <TableHead className="min-w-32 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.job')}
              </TableHead>
              <TableHead className="min-w-32 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.candidate')}
              </TableHead>
              <TableHead className="w-full text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.notes')}
              </TableHead>
              <TableHead className="min-w-40 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('admin.feedback.columns.date')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feedback.map((entry) => (
              <FeedbackRow key={entry.id} entry={entry} />
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {(page - 1) * limit + 1}–{Math.min(page * limit, total)} / {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                onPageChange(page - 1);
              }}
            >
              {t('pagination.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => {
                onPageChange(page + 1);
              }}
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackRow({ entry }: { entry: FeedbackEntry }): React.ReactNode {
  const { t } = useTranslation();
  const appUrl = `/org/${entry.orgSlug}/department/${entry.deptSlug}/jobs/${entry.jobId}/${entry.applicationId}`;

  return (
    <TableRow
      className={`group cursor-pointer ${entry.thumbs === 'down' ? 'bg-destructive/5 hover:bg-destructive/10' : ''}`}
      onClick={() => {
        window.location.href = appUrl;
      }}
    >
      <TableCell className="py-3">
        {entry.thumbs === 'up' ? (
          <ThumbsUp
            className="size-4 text-emerald-500"
            aria-label={t('admin.feedback.thumbs.up')}
          />
        ) : (
          <ThumbsDown
            className="size-4 text-destructive"
            aria-label={t('admin.feedback.thumbs.down')}
          />
        )}
      </TableCell>
      <TableCell className="py-3">
        <Badge variant="secondary">
          {entry.modality === 'voice'
            ? t('admin.feedback.modality.voice')
            : t('admin.feedback.modality.text')}
        </Badge>
      </TableCell>
      <TableCell className="max-w-32 py-3 text-sm text-muted-foreground">
        <span className="block truncate" title={entry.orgName}>
          {entry.orgName}
        </span>
      </TableCell>
      <TableCell className="max-w-40 py-3 text-sm text-muted-foreground">
        <span className="block truncate" title={entry.jobTitle}>
          {entry.jobTitle}
        </span>
      </TableCell>
      <TableCell className="max-w-36 py-3 text-sm text-muted-foreground">
        <span className="block truncate" title={entry.candidateName}>
          {entry.candidateName}
        </span>
      </TableCell>
      <TableCell className="whitespace-normal py-3 text-sm text-muted-foreground">
        {entry.notes ?? <span className="italic">{t('admin.feedback.noNotes')}</span>}
      </TableCell>
      <TableCell className="py-3 text-sm text-muted-foreground">
        {formatDate(entry.createdAt)}
      </TableCell>
    </TableRow>
  );
}
